import { Fragment, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Button,
  Form,
  ListGroup,
  ListGroupItem,
  Offcanvas,
} from "react-bootstrap";
import {
  BsBox,
  BsCalendarCheck,
  BsCalendarDate,
  BsCaretDownFill,
  BsCaretUpFill,
  BsCheckSquare,
  BsPerson,
  BsSignpost2,
  BsTruck,
} from "react-icons/bs";
import styles from "./bookingFilters.module.scss";
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from "react-redux";
import bookingFilterSlice from "./store/slice";
import { Status } from "../statuses/store/slice";
import { VehicleType } from "../vehicle-types/store/slice";
import { CargoType } from "../cargo-types/store/slice";
import moment from "moment";
import { isTodayUTC, isTomorrowUTC } from "../../utils/date";
import { Route } from "../routes/store/slice";
import InternalBadge from "../internal-badge/InternalBadge";
import Select, { ActionMeta, MultiValue, StylesConfig } from "react-select";
import userSlice, { RoleCode, User } from "../users/store/slice";
import defaultSelectStyle from "../../utils/select";
import { groupBy, uniqBy } from "lodash";
import StatusBadge from "../status-badge/StatusBadge";
import { hasRole } from "../../utils/roles";
import { UserRequestParams } from "../users/store/saga";
import { TrailerType } from "../trailer-types/store/slice";
import CargoBadge from "../cargo-badge/CargoBadge";
import { FaTrailer } from "react-icons/fa";

type CustomerOption = {
  label: string;
  value: User;
};

export enum FilterCode {
  Departure = "departure",
  Arrival = "arrival",
  // Time = "time",
  Route = "route",
  Status = "status",
  VehicleType = "vehicleType",
  TrailerType = "trailerType",
  CargoType = "cargoType",
  Customer = "customer",
}

type Filter = {
  name: string;
  code: FilterCode;
};

const filters: Filter[] = [
  { name: "Departure", code: FilterCode.Departure },
  { name: "Arrival", code: FilterCode.Arrival },
  // { name: "Time", code: FilterCode.Time },
  { name: "Route", code: FilterCode.Route },
  { name: "Status", code: FilterCode.Status },
  { name: "Vehicle type", code: FilterCode.VehicleType },
  { name: "Trailer type", code: FilterCode.TrailerType },
  { name: "Cargo type", code: FilterCode.CargoType },
  { name: "Customer", code: FilterCode.Customer },
];

type Props = {
  show: boolean;
  handleClose: () => void;
};

const BookingFilters = ({ show, handleClose }: Props) => {
  const dispatch = useDispatch();

  const profile = useSelector((state) => state.loginPage.profile);

  const openedFilters = useSelector(
    (state) => state.bookingFilters.openedFilters
  );
  const departureFrom = useSelector(
    (state) => state.bookingFilters.departureFrom
  );

  const departureTo = useSelector((state) => state.bookingFilters.departureTo);
  const arrivalFrom = useSelector((state) => state.bookingFilters.arrivalFrom);
  const arrivalTo = useSelector((state) => state.bookingFilters.arrivalTo);
  // const startTime = useSelector((state) => state.bookingFilters.startTime);
  // const endTime = useSelector((state) => state.bookingFilters.endTime);
  const personalizedFilters = useSelector((state) => state.userFilters.data);
  const selectedRoutes = useSelector(
    (state) => state.bookingFilters.selectedRoutes
  );
  const statuses = useSelector((state) => state.statuses.data);
  const selectedStatuses = useSelector(
    (state) => state.bookingFilters.selectedStatuses
  );
  const vehicleTypes = useSelector((state) => state.vehicleTypes.data);
  const selectedVehicleTypes = useSelector(
    (state) => state.bookingFilters.selectedVehicleTypes
  );
  const trailerTypes = useSelector((state) => state.trailerTypes.data);
  const selectedTrailerTypes = useSelector(
    (state) => state.bookingFilters.selectedTrailerTypes
  );
  const cargoTypes = useSelector((state) => state.cargoTypes.data);
  const selectedCargoTypes = useSelector(
    (state) => state.bookingFilters.selectedCargoTypes
  );
  const archived = useSelector((state) => state.bookingFilters.archived);
  const createdByMe = useSelector((state) => state.bookingFilters.createdByMe);
  const imported = useSelector((state) => state.bookingFilters.imported);
  const users = useSelector((state) => state.users.data);
  const usersLoading = useSelector((state) => state.users.isLoading);
  const selectedCustomers = useSelector(
    (state) => state.bookingFilters.selectedCustomers
  );
  const customerName = useSelector(
    (state) => state.bookingFilters.customerName
  );

  const toggleFilter = useCallback(
    (code: FilterCode) =>
      dispatch(bookingFilterSlice.actions.toggleFilter(code)),
    [dispatch]
  );
  const toggleRoute = useCallback(
    (route: Route) => dispatch(bookingFilterSlice.actions.toggleRoute(route)),
    [dispatch]
  );
  const toggleStatus = useCallback(
    (status: Status) =>
      dispatch(bookingFilterSlice.actions.toggleStatus(status)),
    [dispatch]
  );
  const toggleVehicleType = useCallback(
    (type: VehicleType) =>
      dispatch(bookingFilterSlice.actions.toggleVehicleType(type)),
    [dispatch]
  );
  const toggleTrailerType = useCallback(
    (type: TrailerType) =>
      dispatch(bookingFilterSlice.actions.toggleTrailerType(type)),
    [dispatch]
  );
  const toggleCargoType = useCallback(
    (type: CargoType) =>
      dispatch(bookingFilterSlice.actions.toggleCargoType(type)),
    [dispatch]
  );
  const setDepartureFrom = useCallback(
    (date: Date | null) =>
      dispatch(bookingFilterSlice.actions.setDepartureFrom(date)),
    [dispatch]
  );
  const setDepartureTo = useCallback(
    (date: Date | null) =>
      dispatch(bookingFilterSlice.actions.setDepartureTo(date)),
    [dispatch]
  );
  const setArrivalFrom = useCallback(
    (date: Date | null) =>
      dispatch(bookingFilterSlice.actions.setArrivalFrom(date)),
    [dispatch]
  );
  const setArrivalTo = useCallback(
    (date: Date | null) =>
      dispatch(bookingFilterSlice.actions.setArrivalTo(date)),
    [dispatch]
  );
  // const setStartTime = useCallback(
  //   (date: Date | null) =>
  //     dispatch(bookingFilterSlice.actions.setStartTime(date)),
  //   [dispatch]
  // );
  // const setEndTime = useCallback(
  //   (date: Date | null) =>
  //     dispatch(bookingFilterSlice.actions.setEndTime(date)),
  //   [dispatch]
  // );
  const toggleArchived = useCallback(
    () => dispatch(bookingFilterSlice.actions.toggleArchived()),
    [dispatch]
  );
  const toggleCreatedByMe = useCallback(
    () => dispatch(bookingFilterSlice.actions.toggleCreatedByMe()),
    [dispatch]
  );
  const toggleImported = useCallback(
    () => dispatch(bookingFilterSlice.actions.toggleImported()),
    [dispatch]
  );
  const fetchUsers = useCallback(
    (search: string) =>
      dispatch(
        userSlice.actions.fetch({
          search,
          roleCode: RoleCode.Customer,
          onlyWithCompanies: true,
        } as UserRequestParams)
      ),
    [dispatch]
  );
  const resetUsers = useCallback(
    () => dispatch(userSlice.actions.reset()),
    [dispatch]
  );
  const setCustomers = useCallback(
    (customers: User[]) =>
      dispatch(bookingFilterSlice.actions.setCustomers(customers)),
    [dispatch]
  );
  const setCustomerName = useCallback(
    (name: String) =>
      dispatch(bookingFilterSlice.actions.setCustomerName(name)),
    [dispatch]
  );

  const handleCustomerInputChange = (inputValue: string, actionMeta: any) => {
    if (actionMeta.action === "input-change") {
      setCustomerName(inputValue);
      fetchUsers(inputValue);
    }
  };

  const handleCustomerChange = (
    values: MultiValue<CustomerOption>,
    actionMeta: ActionMeta<CustomerOption>
  ) => {
    if (actionMeta.action !== "pop-value") {
      setCustomers(values.map((customerOption) => customerOption.value));
      setCustomerName("");
    }
  };

  const selectedCustomerOptions: CustomerOption[] = useMemo(
    () =>
      selectedCustomers.map((user) => ({
        label: user.name,
        value: user,
      })),
    [selectedCustomers]
  );

  const customerOptions: any = useMemo(
    () =>
      uniqBy(
        users.flatMap((user) => user.companies!),
        "ax_code"
      ).map((uniqueCompany) => ({
        label: `${uniqueCompany.name} (${uniqueCompany.ax_code})`,
        options: users
          .filter((user) =>
            user.companies!.find(
              (company) => company.ax_code === uniqueCompany.ax_code
            )
          )
          .map((user) => ({
            label: user.name,
            value: user,
          })),
      })),
    [users]
  );

  const departurePickerRef = useRef(null);

  const departurePickerRefCallback = useCallback((node: any) => {
    if (node !== null) {
      node.setPreSelection(null);
      departurePickerRef.current = node;
    }
  }, []);

  const setDeparturePickerPreselection = (date: Date | null) => {
    if (departurePickerRef && departurePickerRef.current) {
      (departurePickerRef as any).current.setPreSelection(date);
    }
  };

  const arrivalPickerRef = useRef(null);

  const arrivalPickerRefCallback = useCallback((node: any) => {
    if (node !== null) {
      node.setPreSelection(null);
      arrivalPickerRef.current = node;
    }
  }, []);

  const setArrivalPickerPreselection = (date: Date | null) => {
    if (arrivalPickerRef && arrivalPickerRef.current) {
      (arrivalPickerRef as any).current.setPreSelection(date);
    }
  };

  const setAllDays = (code: FilterCode) => {
    if (code === FilterCode.Departure) {
      setDepartureFrom(null);
      setDepartureTo(null);
      setDeparturePickerPreselection(null);
    } else if (code === FilterCode.Arrival) {
      setArrivalFrom(null);
      setArrivalTo(null);
      setArrivalPickerPreselection(null);
    }
  };

  const setToday = (code: FilterCode) => {
    const today = moment().toDate();
    const tomorrow = moment().add(1, "day").toDate();

    if (code === FilterCode.Departure) {
      setDepartureFrom(today);
      setDepartureTo(tomorrow);
      setDeparturePickerPreselection(tomorrow);
    } else if (code === FilterCode.Arrival) {
      setArrivalFrom(today);
      setArrivalTo(tomorrow);
      setArrivalPickerPreselection(tomorrow);
    }
  };

  const setTomorrow = (code: FilterCode) => {
    const tomorrow = moment().add(1, "day").toDate();
    const afterTomorrow = moment().add(2, "day").toDate();

    if (code === FilterCode.Departure) {
      setDepartureFrom(tomorrow);
      setDepartureTo(afterTomorrow);
      setDeparturePickerPreselection(afterTomorrow);
    } else if (code === FilterCode.Arrival) {
      setArrivalFrom(tomorrow);
      setArrivalTo(afterTomorrow);
      setArrivalPickerPreselection(afterTomorrow);
    }
  };

  const highlightedDepartureDates = useMemo(() => {
    if (!personalizedFilters) return [];

    return personalizedFilters.departure_dates.map((date) =>
      moment(date).toDate()
    );
  }, [personalizedFilters]);

  const highlightedArrivalDates = useMemo(() => {
    if (!personalizedFilters) return [];

    return personalizedFilters.arrival_dates.map((date) =>
      moment(date).toDate()
    );
  }, [personalizedFilters]);

  useEffect(() => {
    return () => {
      resetUsers();
    };
  }, [resetUsers]);

  if (!profile) return null;

  return (
    <Offcanvas show={show} onHide={handleClose} scroll>
      <Offcanvas.Header closeButton className={styles.header}>
        <Offcanvas.Title>Filters</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className={styles.body}>
        <ListGroup className="shadow-sm" variant="flush">
          {filters.map((filter) => {
            if (
              filter.code === FilterCode.Customer &&
              !hasRole(profile, RoleCode.Internal)
            )
              return null;

            const isFilterOpened = openedFilters.includes(filter.code);

            return (
              <Fragment key={filter.code}>
                <ListGroupItem
                  action
                  onClick={() => toggleFilter(filter.code)}
                  className={styles.item}
                >
                  <span className={styles.filterTitle}>
                    {filter.code === FilterCode.Departure && <BsCalendarDate />}
                    {filter.code === FilterCode.Arrival && <BsCalendarCheck />}
                    {filter.code === FilterCode.Route && <BsSignpost2 />}
                    {filter.code === FilterCode.Status && <BsCheckSquare />}
                    {filter.code === FilterCode.VehicleType && <BsTruck />}
                    {filter.code === FilterCode.TrailerType && <FaTrailer />}
                    {filter.code === FilterCode.CargoType && <BsBox />}
                    {filter.code === FilterCode.Customer && <BsPerson />}

                    {filter.name}

                    {filter.code === FilterCode.Customer && (
                      <InternalBadge className={styles.internalBadge} />
                    )}
                  </span>
                  <span className="text-primary">
                    {isFilterOpened ? <BsCaretUpFill /> : <BsCaretDownFill />}
                  </span>
                </ListGroupItem>
                {!!(isFilterOpened && filter.code === FilterCode.Departure) && (
                  <ListGroupItem className={styles.dateContent}>
                    <DatePicker
                      ref={departurePickerRefCallback}
                      onChange={([start, end]) => {
                        setDepartureFrom(start);
                        setDepartureTo(end);
                      }}
                      startDate={departureFrom}
                      endDate={departureTo}
                      highlightDates={highlightedDepartureDates}
                      selectsRange
                      inline
                      showMonthDropdown
                      showYearDropdown
                    />
                    <div className={styles.dateButtons}>
                      <Button
                        size="sm"
                        variant="light"
                        onClick={() => setAllDays(FilterCode.Departure)}
                        disabled={!departureFrom && !departureTo}
                      >
                        All days
                      </Button>
                      <Button
                        size="sm"
                        variant="info"
                        onClick={() => setToday(FilterCode.Departure)}
                        disabled={isTodayUTC(departureFrom, departureTo)}
                      >
                        Today
                      </Button>
                      <Button
                        size="sm"
                        variant="info"
                        onClick={() => setTomorrow(FilterCode.Departure)}
                        disabled={isTomorrowUTC(departureFrom, departureTo)}
                      >
                        Tomorrow
                      </Button>
                    </div>
                  </ListGroupItem>
                )}

                {!!(isFilterOpened && filter.code === FilterCode.Arrival) && (
                  <ListGroupItem className={styles.dateContent}>
                    <DatePicker
                      ref={arrivalPickerRefCallback}
                      onChange={([start, end]) => {
                        setArrivalFrom(start);
                        setArrivalTo(end);
                      }}
                      startDate={arrivalFrom}
                      endDate={arrivalTo}
                      highlightDates={highlightedArrivalDates}
                      selectsRange
                      inline
                      showMonthDropdown
                      showYearDropdown
                    />
                    <div className={styles.dateButtons}>
                      <Button
                        size="sm"
                        variant="light"
                        onClick={() => setAllDays(FilterCode.Arrival)}
                        disabled={!arrivalFrom && !arrivalTo}
                      >
                        All days
                      </Button>
                      <Button
                        size="sm"
                        variant="info"
                        onClick={() => setToday(FilterCode.Arrival)}
                        disabled={isTodayUTC(arrivalFrom, arrivalTo)}
                      >
                        Today
                      </Button>
                      <Button
                        size="sm"
                        variant="info"
                        onClick={() => setTomorrow(FilterCode.Arrival)}
                        disabled={isTomorrowUTC(arrivalFrom, arrivalTo)}
                      >
                        Tomorrow
                      </Button>
                    </div>
                  </ListGroupItem>
                )}

                {/* {!!(isFilterOpened && filter.code === FilterCode.Time) && (
                  <ListGroupItem className={styles.timeContent}>
                    <Form.Control
                      as={DatePicker}
                      selected={startTime}
                      onChange={(date: any) => setStartTime(date)}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      timeCaption="Time"
                      timeFormat="HH:mm"
                      dateFormat="HH:mm"
                      wrapperClassName={styles.timeWrapper}
                      popperClassName={styles.timePopper}
                      className={styles.timeInput}
                      placeholderText="Start time"
                    />

                    <Form.Control
                      as={DatePicker}
                      selected={endTime}
                      onChange={(date: any) => setEndTime(date)}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      timeCaption="Time"
                      timeFormat="HH:mm"
                      dateFormat="HH:mm"
                      popperClassName={styles.timePopper}
                      className={styles.timeInput}
                      placeholderText="End time"
                    />
                  </ListGroupItem>
                )} */}

                {!!(isFilterOpened && filter.code === FilterCode.Route) && (
                  <>
                    {personalizedFilters &&
                    personalizedFilters.routes.length > 0 ? (
                      personalizedFilters.routes.map((route, index) => (
                        <ListGroupItem
                          key={index}
                          className={styles.filterItem}
                        >
                          <Form.Check
                            id={`route-${route.id}-switch`}
                            type="switch"
                            label={route.description}
                            onChange={() => toggleRoute(route)}
                            checked={
                              !!selectedRoutes.find(
                                (selectedRoute) => selectedRoute.id === route.id
                              )
                            }
                          />
                        </ListGroupItem>
                      ))
                    ) : (
                      <ListGroupItem className={styles.filterItemMuted}>
                        No bookings found.
                      </ListGroupItem>
                    )}
                  </>
                )}

                {!!(isFilterOpened && filter.code === FilterCode.Status) &&
                  Object.entries(groupBy(statuses, "group.name")).map(
                    ([groupName, statuses], index) => (
                      <Fragment key={index}>
                        <ListGroupItem className={styles.filterItemHeader}>
                          {groupName}:
                        </ListGroupItem>
                        {statuses.map((status, index) => (
                          <ListGroupItem
                            key={index}
                            className={styles.filterItem}
                          >
                            <Form.Check
                              id={`status-${status.code}-switch`}
                              type="switch"
                              label={<StatusBadge showText status={status} />}
                              onChange={() => toggleStatus(status)}
                              checked={
                                !!selectedStatuses.find(
                                  (selectedStatus) =>
                                    selectedStatus.code === status.code
                                )
                              }
                            />
                          </ListGroupItem>
                        ))}
                      </Fragment>
                    )
                  )}

                {!!(isFilterOpened && filter.code === FilterCode.VehicleType) &&
                  vehicleTypes.map((vehicleType, index) => (
                    <ListGroupItem key={index} className={styles.filterItem}>
                      <Form.Check
                        id={`vehicleType-${vehicleType.code}-switch`}
                        type="switch"
                        label={vehicleType.name}
                        onChange={() => toggleVehicleType(vehicleType)}
                        checked={
                          !!selectedVehicleTypes.find(
                            (seletedVehicleType) =>
                              seletedVehicleType.code === vehicleType.code
                          )
                        }
                      />
                    </ListGroupItem>
                  ))}

                {!!(isFilterOpened && filter.code === FilterCode.TrailerType) &&
                  trailerTypes.map((trailerType, index) => (
                    <ListGroupItem key={index} className={styles.filterItem}>
                      <Form.Check
                        id={`trailerType-${trailerType.code}-switch`}
                        type="switch"
                        label={trailerType.name}
                        onChange={() => toggleTrailerType(trailerType)}
                        checked={
                          !!selectedTrailerTypes.find(
                            (seletedTrailerType) =>
                              seletedTrailerType.code === trailerType.code
                          )
                        }
                      />
                    </ListGroupItem>
                  ))}

                {!!(isFilterOpened && filter.code === FilterCode.CargoType) &&
                  cargoTypes.map((cargoType, index) => (
                    <ListGroupItem key={index} className={styles.filterItem}>
                      <Form.Check
                        id={`cargoType-${cargoType.code}-switch`}
                        type="switch"
                        label={<CargoBadge type={cargoType} />}
                        onChange={() => toggleCargoType(cargoType)}
                        checked={
                          !!selectedCargoTypes.find(
                            (selectedCargoType) =>
                              selectedCargoType.code === cargoType.code
                          )
                        }
                      />
                    </ListGroupItem>
                  ))}

                {!!(
                  isFilterOpened &&
                  filter.code === FilterCode.Customer &&
                  hasRole(profile, RoleCode.Internal)
                ) && (
                  <ListGroupItem className={styles.filterItem}>
                    <Select
                      value={selectedCustomerOptions}
                      inputValue={customerName}
                      onInputChange={handleCustomerInputChange}
                      onChange={handleCustomerChange}
                      options={customerOptions}
                      isLoading={usersLoading}
                      placeholder="Search"
                      styles={{
                        ...(defaultSelectStyle as StylesConfig<CustomerOption>),
                        input: (base) => ({
                          ...base,
                        }),
                      }}
                      isMulti
                    />
                  </ListGroupItem>
                )}
              </Fragment>
            );
          })}
        </ListGroup>

        <div className={styles.outsideFilters}>
          <Form.Check
            type="switch"
            id={`archived-switch`}
            label="Archived"
            checked={archived}
            onChange={toggleArchived}
          />
          <Form.Check
            type="switch"
            id={`my-created-switch`}
            label="Created by me"
            checked={createdByMe}
            onChange={toggleCreatedByMe}
          />
          <Form.Check
            type="switch"
            id={`imported-switch`}
            label="Imported"
            checked={imported}
            onChange={toggleImported}
          />
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default BookingFilters;
